import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

export interface RegistrationItem {
  path: string;
  status: 'pending' | 'registering' | 'complete' | 'failed' | 'env_leak';
  codebaseName?: string;
  error?: string;
  detectedKeys?: { name: string; file: string }[];
  migratedKeys?: { file: string; key: string }[];
}

export interface RegistrationBatch {
  batchId: string;
  items: RegistrationItem[];
  completed: number;
  failed: number;
  total: number;
  startedAt: number;
}

export interface RecentBatch {
  batchId: string;
  completed: number;
  failed: number;
  total: number;
  completedAt: number;
}

interface RegistrationStoreState {
  activeBatch: RegistrationBatch | null;
  recentBatches: RecentBatch[];
  // Actions
  startBatch: (batchId: string, paths: string[]) => void;
  clearBatch: () => void;
  handleRegistrationStart: (event: RegistrationStartEvent) => void;
  handleRegistrationComplete: (event: RegistrationCompleteEvent) => void;
  handleRegistrationFailed: (event: RegistrationFailedEvent) => void;
  handleRegistrationEnvLeak: (event: RegistrationEnvLeakEvent) => void;
  handleRegistrationProgress: (event: RegistrationProgressEvent) => void;
  handleRegistrationBatchComplete: (event: RegistrationBatchCompleteEvent) => void;
}

// --- SSE Event types ---

interface RegistrationStartEvent {
  type: 'registration_start';
  path: string;
}

interface RegistrationCompleteEvent {
  type: 'registration_complete';
  path: string;
  codebaseName: string;
}

interface RegistrationFailedEvent {
  type: 'registration_failed';
  path: string;
  error: string;
}

interface RegistrationEnvLeakEvent {
  type: 'registration_env_leak';
  path: string;
  detectedKeys: { name: string; file: string }[];
}

interface RegistrationProgressEvent {
  type: 'registration_progress';
  completed: number;
  failed: number;
  total: number;
}

interface RegistrationBatchCompleteEvent {
  type: 'registration_batch_complete';
  batchId: string;
}

// --- Helpers ---

/** Update a single item in the active batch by path. Returns unchanged state if no active batch or path not found. */
function updateItem(
  state: RegistrationStoreState,
  path: string,
  updater: (item: RegistrationItem) => RegistrationItem
): Partial<RegistrationStoreState> | RegistrationStoreState {
  if (!state.activeBatch) return state;
  const idx = state.activeBatch.items.findIndex(item => item.path === path);
  if (idx < 0) return state;
  const items = [...state.activeBatch.items];
  items[idx] = updater(items[idx]);
  return { activeBatch: { ...state.activeBatch, items } };
}

// --- Store ---

export const useRegistrationStore = create<RegistrationStoreState>()(
  devtools(
    subscribeWithSelector(set => ({
      activeBatch: null,
      recentBatches: [],

      startBatch: (batchId: string, paths: string[]): void => {
        set(
          () => ({
            activeBatch: {
              batchId,
              items: paths.map(path => ({ path, status: 'pending' as const })),
              completed: 0,
              failed: 0,
              total: paths.length,
              startedAt: Date.now(),
            },
          }),
          undefined,
          'registration/startBatch'
        );
      },

      clearBatch: (): void => {
        set(() => ({ activeBatch: null }), undefined, 'registration/clearBatch');
      },

      handleRegistrationStart: (event: RegistrationStartEvent): void => {
        set(
          state => updateItem(state, event.path, item => ({ ...item, status: 'registering' })),
          undefined,
          'registration/start'
        );
      },

      handleRegistrationComplete: (event: RegistrationCompleteEvent): void => {
        set(
          state =>
            updateItem(state, event.path, item => ({
              ...item,
              status: 'complete',
              codebaseName: event.codebaseName,
            })),
          undefined,
          'registration/complete'
        );
      },

      handleRegistrationFailed: (event: RegistrationFailedEvent): void => {
        set(
          state =>
            updateItem(state, event.path, item => ({
              ...item,
              status: 'failed',
              error: event.error,
            })),
          undefined,
          'registration/failed'
        );
      },

      handleRegistrationEnvLeak: (event: RegistrationEnvLeakEvent): void => {
        set(
          state =>
            updateItem(state, event.path, item => ({
              ...item,
              status: 'env_leak',
              detectedKeys: event.detectedKeys,
            })),
          undefined,
          'registration/envLeak'
        );
      },

      handleRegistrationProgress: (event: RegistrationProgressEvent): void => {
        set(
          state => {
            if (!state.activeBatch) return state;
            return {
              activeBatch: {
                ...state.activeBatch,
                completed: event.completed,
                failed: event.failed,
                total: event.total,
              },
            };
          },
          undefined,
          'registration/progress'
        );
      },

      handleRegistrationBatchComplete: (_event: RegistrationBatchCompleteEvent): void => {
        set(
          state => {
            if (!state.activeBatch) return state;
            const recent: RecentBatch = {
              batchId: state.activeBatch.batchId,
              completed: state.activeBatch.completed,
              failed: state.activeBatch.failed,
              total: state.activeBatch.total,
              completedAt: Date.now(),
            };
            return {
              activeBatch: null,
              recentBatches: [recent, ...state.recentBatches],
            };
          },
          undefined,
          'registration/batchComplete'
        );
      },
    })),
    { name: 'RegistrationStore', enabled: import.meta.env.DEV }
  )
);

// Stable SSE handler object — actions are defined once in create(), so references never change.
const {
  handleRegistrationStart,
  handleRegistrationComplete,
  handleRegistrationFailed,
  handleRegistrationEnvLeak,
  handleRegistrationProgress,
  handleRegistrationBatchComplete,
} = useRegistrationStore.getState();

export const registrationSSEHandlers = {
  onRegistrationStart: handleRegistrationStart,
  onRegistrationComplete: handleRegistrationComplete,
  onRegistrationFailed: handleRegistrationFailed,
  onRegistrationEnvLeak: handleRegistrationEnvLeak,
  onRegistrationProgress: handleRegistrationProgress,
  onRegistrationBatchComplete: handleRegistrationBatchComplete,
} as const;
